import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaSort
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const EngineerSubWorksDistributionList = () => {
    const navigate = useNavigate();
    const [taluka, setTaluka] = useState('तालुका निवडा');
    const [year, setYear] = useState('२०२६');
    const [searchTerm, setSearchTerm] = useState('');
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    const talukaMapping = {
        'Miraj': 'मिरज',
        'Tasgaon': 'तासगाव',
        'Kavathe Mahankal': 'कवठे महांकाळ',
        'Walwa': 'वाळवा',
        'Shirala': 'शिराळा',
        'Jat': 'जत',
        'Khanapur': 'खानापूर',
        'Atpadi': 'आटपाडी',
        'Palus': 'पलूस',
        'Kadegaon': 'कडेगाव',
        'Purandar': 'पुरंदर',
        'Ambegaon': 'आंबेगाव',
        'Shirur': 'शिरूर',
        'Daund': 'दौंड',
        'Baramati': 'बारामती',
        'Junnar': 'जुन्नर',
        'Haveli': 'हवेली',
        'Indapur': 'इंदापूर',
        'Mulshi': 'मुळशी',
        'Bhor': 'भोर',
        'Maval': 'मावळ',
        'Velhe': 'वेल्हे',
        'Khed': 'खेड'
    };

    const getMarathiTaluka = (t) => talukaMapping[t] || t;

    useEffect(() => {
        setLoading(true);
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {};
        let socData = {};
        let engDone = false;
        let socDone = false;

        const processResults = () => {
            if (engDone && socDone) {
                const combined = [
                    ...Object.entries(engData).map(([id, val]) => ({ 
                        id, 
                        ...val, 
                        type: 'Engineer',
                        displayName: val.firstName ? `${val.firstName} ${val.surname}` : (val.fullName || val.name || "-")
                    })),
                    ...Object.entries(socData).map(([id, val]) => ({ 
                        id, 
                        ...val, 
                        type: 'Society',
                        displayName: val.societyName || val.fullName || val.name || "-"
                    }))
                ];

                setRegistrations(combined);
                setLoading(false);
            }
        };

        const engUnsubscribe = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            processResults();
        });

        const socUnsubscribe = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            processResults();
        });

        return () => {
            engUnsubscribe();
            socUnsubscribe();
        };
    }, []);

    const filteredData = useMemo(() => {
        // Convert selected Marathi year to English digits (e.g., "२०२६" -> "2026")
        const engYear = year.split('').map(char => {
            const idx = marathiNumerals.indexOf(char);
            return idx !== -1 ? String(idx) : char;
        }).join('');

        const processed = registrations.map(user => {
            let totalCount = 0;
            let totalAmount = 0;
            
            if (user.GeneratedLottery) {
                Object.entries(user.GeneratedLottery).forEach(([dateKey, lotteryData]) => {
                    // Check if the date string ends with the current selected year
                    if (dateKey.endsWith(`-${engYear}`) && lotteryData && typeof lotteryData === 'object') {
                        totalCount++;
                        const amount = parseFloat(lotteryData.tenderAmount || lotteryData.price || 0);
                        totalAmount += amount;
                    }
                });
            }

            return {
                ...user,
                workCount: totalCount,
                totalAmount: totalAmount
            };
        });

        return processed.filter(user => {
            const matchesTaluka = taluka === 'तालुका निवडा' || (user.taluka && getMarathiTaluka(user.taluka) === taluka);
            const matchesSearch = user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 (user.whatsappNo && user.whatsappNo.includes(searchTerm)) ||
                                 (user.mobile && user.mobile.includes(searchTerm));
            const hasWork = user.workCount > 0;
            
            return matchesTaluka && matchesSearch && hasWork;
        });
    }, [registrations, taluka, searchTerm, year]);

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'अर्जदाराचे नाव': item.displayName,
            'फोन नंबर': item.whatsappNo || item.mobile || "-",
            'तालुका': getMarathiTaluka(item.taluka || "-"),
            'कामांची संख्या': item.workCount,
            'कामाची रक्कम': item.totalAmount.toLocaleString('en-IN'),
            'परवाना तारीख': item.licenseValidity || "-",
            'तारीख जोडा': item.registrationDate || "-"
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sub_Works_Distribution");
        XLSX.writeFile(wb, `Sub_Works_Distribution_${new Date().toLocaleDateString()}.xlsx`);
        toast.info("एक्सेल फाईल डाउनलोड होत आहे...");
    };

    const copyToClipboard = () => {
        const headers = ['अ.क्र.', 'अर्जदाराचे नाव', 'फोन नंबर', 'तालुका', 'कामांची संख्या', 'कामाची रक्कम'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.displayName,
            item.whatsappNo || item.mobile || "-",
            getMarathiTaluka(item.taluka || "-"),
            item.workCount,
            item.totalAmount
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("टेबल डेटा कॉपी झाला!");
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Top Header - Image Style */}
                <div className="flex justify-between items-start mb-6 px-2 no-print">
                    <h1 className="text-xl md:text-2xl font-bold text-gray-700">सब वर्क्स वितरण यादी</h1>
                    <div className="text-[12px] md:text-[14px] text-gray-500 font-medium">
                        जिल्हा परिषद / <span className="text-gray-400">सब वर्क्स वितरण यादी</span>
                    </div>
                </div>

                {/* Main Container - Card Style */}
                <div className="bg-white rounded shadow-sm border border-gray-100 p-6 md:p-10 mb-8">
                    {/* Filters Section */}
                    <div className="flex flex-wrap gap-6 items-end mb-8 no-print">
                        <div className="flex flex-col gap-2 min-w-[250px] md:min-w-[400px]">
                            <label className="text-sm font-medium text-gray-600">तालुका निवडा</label>
                            <select 
                                className="border border-gray-300 rounded px-4 py-2 outline-none focus:border-blue-500 bg-white text-gray-700 font-bold appearance-none w-full"
                                value={taluka}
                                onChange={(e) => setTaluka(e.target.value)}
                            >
                                <option value="तालुका निवडा">तालुका निवडा</option>
                                <option value="मिरज">मिरज</option>
                                <option value="तासगाव">तासगाव</option>
                                <option value="कवठे महांकाळ">कवठे महांकाळ</option>
                                <option value="वाळवा">वाळवा</option>
                                <option value="शिराळा">शिराळा</option>
                                <option value="जत">जत</option>
                                <option value="खानापूर">खानापूर</option>
                                <option value="आटपाडी">आटपाडी</option>
                                <option value="पलूस">पलूस</option>
                                <option value="कडेगाव">कडेगाव</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-2 min-w-[200px] md:min-w-[400px]">
                            <label className="text-sm font-medium text-gray-600">वर्ष निवडा</label>
                            <select 
                                className="border border-gray-300 rounded px-4 py-2 outline-none focus:border-blue-500 bg-white text-gray-700 font-bold appearance-none w-full"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                <option value="२०२६">२०२६</option>
                                <option value="२०२५">२०२५</option>
                                <option value="२०२४">२०२४</option>
                            </select>
                        </div>
                        <button className="bg-[#4e73df] hover:bg-blue-700 text-white px-8 py-2.5 rounded font-bold shadow-sm transition active:scale-95 text-sm uppercase">
                            शोध
                        </button>
                    </div>

                    {/* Button Toolbar & Search */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4 no-print mt-10">
                        <div className="flex rounded overflow-hidden shadow-sm border border-gray-400">
                            <button 
                                onClick={copyToClipboard}
                                className="bg-[#6c757d] hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold border-r border-gray-500"
                            >
                                कॉपी करा
                            </button>
                            <button 
                                onClick={downloadExcel}
                                className="bg-[#6c757d] hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold border-r border-gray-500"
                            >
                                एक्सेल
                            </button>
                            <button 
                                onClick={handlePrint}
                                className="bg-[#6c757d] hover:bg-gray-700 text-white px-4 py-2 text-xs font-bold"
                            >
                                प्रिंट
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-bold text-gray-600">शोध:</label>
                            <input 
                                type="text" 
                                className="border border-gray-300 rounded px-4 py-1.5 w-64 md:w-80 outline-none focus:border-blue-500 bg-white text-sm font-bold"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto border-t border-gray-200 mt-6">
                        <table className="w-full border-collapse border border-gray-300">
                            <thead className="bg-white">
                                <tr className="text-[13px] font-black text-gray-700 text-left">
                                    <th className="px-3 py-4 border border-gray-300 min-w-[40px] text-center"># <FaSort className="inline ml-1 text-gray-200" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[200px]">अर्जदाराचे नाव <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[150px]">फोन नंबर <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[120px]">तालुका <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[200px]">कामांची संख्या <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[150px]">कामाची रक्कम <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[150px]">परवाना तारीख <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                    <th className="px-4 py-4 border border-gray-300 min-w-[150px]">तारीख जोडा <FaSort className="inline ml-1 text-gray-200 float-right" size={10} /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length > 0 ? filteredData.map((item, index) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors text-[14px] text-gray-700 font-bold">
                                        <td className="px-3 py-4 border border-gray-300 text-center">{toMarathiNumber(index + 1)}</td>
                                        <td className="px-4 py-4 border border-gray-300">{item.displayName}</td>
                                        <td className="px-4 py-4 border border-gray-300">{toMarathiNumber(item.whatsappNo || item.mobile || "-")}</td>
                                        <td className="px-4 py-4 border border-gray-300">{getMarathiTaluka(item.taluka || "-")}</td>
                                        <td className="px-4 py-4 border border-gray-300">
                                            <div className="flex items-center gap-2">
                                                {toMarathiNumber(item.workCount)} - <button 
                                                    onClick={() => navigate(`/engineer/subhe-work-detail/${item.id}`)}
                                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                                >
                                                    कामाचे तपशील
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 border border-gray-300">{item.totalAmount > 0 ? toMarathiNumber(item.totalAmount.toLocaleString('en-IN')) : "०"}</td>
                                        <td className="px-4 py-4 border border-gray-300">{item.licenseValidity || item.licenseDate || "-"}</td>
                                        <td className="px-4 py-4 border border-gray-300">{item.registrationDate || item.joinDate || "-"}</td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="8" className="px-5 py-20 text-center text-gray-400 font-bold">
                                            कोणतीही माहिती उपलब्ध नाही.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                table {
                    border-collapse: collapse;
                }
                th {
                    background-color: white !important;
                    color: #555 !important;
                }
                td, th {
                    border: 1px solid #dee2e6 !important;
                }
                tr:nth-child(even) {
                    background-color: #f8f9fa;
                }
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .bg-[#f4f7fe] { background: white !important; }
                    .p-8, .p-4 { padding: 0 !important; }
                    .shadow-sm { box-shadow: none !important; }
                    .border { border: 1px solid #eee !important; }
                    table { width: 100% !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubWorksDistributionList;


